import { LifeBuoy, Rocket, User, Truck, Wrench, Store, MessageCircle, Gift } from 'lucide-react';
import type { ReactNode } from 'react';

interface ChapterDef {
  id: string;
  icon: ReactNode;
  label: string;
}

const CHAPTERS: ChapterDef[] = [
  { id: 'getting-started', icon: <Rocket className="w-3.5 h-3.5" />,        label: 'Getting Started' },
  { id: 'customers',       icon: <User className="w-3.5 h-3.5" />,          label: 'For Customers' },
  { id: 'carriers',        icon: <Truck className="w-3.5 h-3.5" />,         label: 'For Carriers' },
  { id: 'workshops',       icon: <Wrench className="w-3.5 h-3.5" />,        label: 'For Workshops' },
  { id: 'bazaar',          icon: <Store className="w-3.5 h-3.5" />,         label: 'The Bazaar' },
  { id: 'chat',            icon: <MessageCircle className="w-3.5 h-3.5" />, label: 'Chat & AI Assistant' },
  { id: 'free',            icon: <Gift className="w-3.5 h-3.5" />,          label: "What's Free" },
];

function Chapter({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Topic({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-1.5">{title}</h3>
      <div className="space-y-2 text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Ul({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc list-inside space-y-1.5 text-gray-600 ml-2">
      {items.map((item, idx) => <li key={idx}>{item}</li>)}
    </ul>
  );
}

export default function HelpPage() {
  return (
    <div className="page-shell-prose max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-1">
          <LifeBuoy className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Help &amp; How-To Guide</h1>
          <p className="text-sm text-gray-500 mt-1">
            What you can do on TransitDeal, role by role — including the features that are free to use.
          </p>
        </div>
      </div>

      {/* Quick jump nav */}
      <nav className="flex flex-wrap gap-2 mb-8">
        {CHAPTERS.map(c => (
          <a
            key={c.id}
            href={`#${c.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200 rounded-full px-3 py-1.5 transition-colors"
          >
            {c.icon}{c.label}
          </a>
        ))}
      </nav>

      <div className="card p-6 sm:p-8">

        <Chapter id="getting-started" title="Getting Started">
          <p>
            TransitDeal has three account types, chosen when you register and fixed afterwards: <strong>Customer</strong> (posts
            jobs and pays for them), <strong>Carrier</strong> (drives and delivers — quotes on jobs and runs a fleet), and{' '}
            <strong>Workshop</strong> (repairs vehicles — quotes on repair jobs). There is no membership fee for any account
            type — registration, browsing, and the core marketplace are free to use.
          </p>

          <Topic title="Creating an account">
            <p>
              Pick your role, enter your email, password, first and last name, and you're straight onto your dashboard —
              no waiting, no payment details required.
            </p>
          </Topic>

          <Topic title="Setting up your profile">
            <Ul items={[
              <>Add your name, phone number, and a one-time Display Name shown on listings, reviews and quotes instead of your real name (choose it carefully — it can't be changed once saved).</>,
              <>Add your address for job matching and invoicing, and your bank/payout details when you're ready to get paid.</>,
              <>Verify your identity with a government ID — required before an individual Customer can pay by Credit, and it's one of the ways a Carrier or Workshop can unlock quoting.</>,
              <>Business accounts can add company details (name, Companies House number, VAT number, director) to unlock business-only features like Credit payment.</>,
            ]} />
          </Topic>

          <Topic title="Getting eligible to quote (Carriers &amp; Workshops)">
            <p>Before you can submit a quote, satisfy any <strong>one</strong> of three routes:</p>
            <Ul items={[
              <>A verified <strong>external rating</strong> of 4★ or better (upload a screenshot from Trustpilot, Google, or Checkatrade).</>,
              <>A <strong>referee code</strong> from an existing member who's completed at least one job.</>,
              <>A fully refundable <strong>security deposit</strong>, paid by card or free bank transfer, returned automatically after 10 successful jobs and a 4★+ rating.</>,
            ]} />
          </Topic>

          <Topic title="Vehicles, insurance &amp; your team">
            <p>
              Carriers register vehicles by number plate — TransitDeal looks the vehicle up automatically against the DVSA
              (make, model, MOT history), so there's nothing to type in by hand. Add drivers or mechanics from the Employees
              page with no limit and no per-seat charge — each one gets their own personal link to run jobs from their phone,
              with no separate TransitDeal account needed.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="customers" title="For Customers">
          <p>
            You post the work — a delivery or a vehicle repair — and TransitDeal's carriers and workshops compete for it.
            Posting a job and reviewing quotes costs nothing; you only pay once you accept one.
          </p>

          <Topic title="Post a delivery or repair job">
            <Ul items={[
              <>Post a <strong>Same-Day</strong> job (a time window today) or a <strong>Scheduled</strong> job (a future date).</>,
              <>Choose a <strong>single pickup/drop-off</strong> or a <strong>multi-drop / multi-pickup</strong> route, and the required vehicle type.</>,
              <>Pay by <strong>Cash</strong> (pay once a carrier is confirmed) or <strong>Credit</strong> (pay after delivery, once your account is verified for it).</>,
              <>An AI "Improve description" button tidies up whatever you type into a clearer, more complete description.</>,
              <>Repair jobs work the same way — workshops quote back with labour and parts priced separately, so you see exactly what you're paying for.</>,
            ]} />
          </Topic>

          <Topic title="Review, chat, and accept quotes">
            <p>
              Every quote shows a price (or, for repairs, a labour/parts breakdown) and a message from the carrier or
              workshop. You can message them directly first — ask about availability or experience — using the built-in
              chat. Accepting a quote confirms what happens next: paying Cash means you'll be prompted to pay and the
              carrier signs the agreement; paying Credit means the carrier accepts first and your contract is issued on
              credit terms, with payment due after delivery.
            </p>
          </Topic>

          <Topic title="Pay for a job">
            <p>
              Payment goes through a secure card checkout. You choose how much to pay <strong>online now</strong> versus{' '}
              <strong>Cash on Delivery</strong> — a minimum online amount applies, up to paying the full amount upfront if
              you'd rather. Several unpaid jobs can be settled together in one checkout, and a Monthly Statement (with CSV
              export) tracks everything you've paid.
            </p>
          </Topic>

          <Topic title="Track your delivery live">
            <p>
              Once the carrier starts the job, your job page shows a live map of their position alongside local traffic
              and weather — genuinely useful context for when it'll actually arrive, not just a status label. If the
              carrier needs to pause mid-route, you'll see an explicit "paused" notice and tracking resumes automatically
              once they're moving again.
            </p>
          </Topic>

          <Topic title="Verify delivery, leave a review, raise a dispute">
            <Ul items={[
              <>Generate a one-time code from your job page to hand to the driver on arrival, as an extra layer of proof alongside their own photo/signature evidence.</>,
              <>Rate the carrier or workshop 1–5 stars with an optional comment once the job's delivered and paid.</>,
              <>Raise a dispute on any completed job — damaged goods, a no-show, a quality issue and more — and Ops will mediate a resolution you can accept or reject.</>,
            ]} />
          </Topic>

          <Topic title="Contracts, invoices, and the Bazaar">
            <p>
              Every accepted job automatically generates a signed legal contract, downloadable from <strong>My
              Contracts</strong>. Providers can also generate a formal invoice you can view, download, or have emailed to
              yourself. And you can buy or sell general items through the <strong>Bazaar</strong> — see that chapter for
              the full flow.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="carriers" title="For Carriers">
          <p>
            As a Carrier you have two ways to use TransitDeal: win open marketplace work by quoting against other
            carriers, or run your own fleet's jobs privately through the platform's tools. The Jobs Board, Route Map,
            Fleet Map, Calendar, Auto-Quote, Available Loads, Job Alerts, and Referrals are all free to use.
          </p>

          <Topic title="Find work on the open marketplace">
            <p>
              The <strong>Jobs Board</strong> tracks every job by status. The <strong>Job Feed</strong> is where you go
              looking for work: search by postcode and radius, then filter hard — favourites only, same-day only, jobs
              with no bids yet, two-man-crew jobs, tail-lift jobs, vehicle type, job type, competing-bid count, route
              length — sorted however suits you. The <strong>Job Route Map</strong> shows the same jobs geographically, so
              you can plan a route across several at once.
            </p>
          </Topic>

          <Topic title="Quote, win, and run the full delivery lifecycle">
            <p>
              Submit a price and an optional message, message the customer before quoting if you want, and edit or
              withdraw a pending quote any time. Once accepted, confirm by assigning a vehicle and, optionally, a driver.
              Starting the job switches on live GPS tracking; at pickup you submit <strong>Proof of Collection</strong>{' '}
              (a photo, the sender's signature and name); you can pause and resume the job for a delay or break; at
              drop-off you submit <strong>Proof of Delivery</strong>, which is what triggers your payment.
            </p>
          </Topic>

          <Topic title="Run your own jobs privately — Internal Fleet Jobs">
            <p>
              When you post a job as a Carrier, an <strong>"Internal Fleet Job"</strong> toggle lets you keep it entirely
              off the public marketplace — nobody else can see or bid on it. Switch it on and the job skips bidding and
              payment entirely: you simply assign one of your own drivers and vehicles straight away, and they get the
              exact same live GPS tracking, Proof of Collection, and Proof of Delivery as any other job. It's a genuinely
              useful fleet-management tool bundled into every Carrier account at no extra cost — handy for relocating your
              own vehicles, running a job for a regular customer relationship that doesn't need to go out to bid, or simply
              trying out the platform's execution tools before you ever post to the open marketplace.
            </p>
          </Topic>

          <Topic title="Let your drivers run jobs from their phones — the Employee Portal">
            <p>
              Every driver or mechanic you add gets a personal link — no TransitDeal account, no login required for them.
              From it they can see their assigned jobs, start a job, submit Proof of Delivery, and share GPS continuously
              so your <strong>Fleet Map</strong> always knows where they are. There's no limit on how many drivers you add.
            </p>
          </Topic>

          <Topic title="Price yourself automatically with Auto-Quote">
            <p>
              Build a standing rate card — distance bands, surcharges for tail-lifts, two-man crews and out-of-hours work,
              a fuel levy, and a price uplift per vehicle type — and matching jobs are quoted automatically, either held
              for your review first or sent instantly. A built-in Simulator lets you test the card before switching it on.
            </p>
          </Topic>

          <Topic title="Fill empty legs with Available Loads, and get notified with Job Alerts">
            <p>
              Got spare space on a route you're already driving? Post it as an Available Load and nearby customers can
              convert it straight into a bookable job. Or save up to 10 postcode/radius Job Alerts and get a push
              notification — and a spoken voice alert on mobile — the instant matching work appears.
            </p>
          </Topic>

          <Topic title="Get paid, and earn from referrals">
            <p>
              The <strong>Payments Board</strong> tracks your available funds, locked deposits, pending releases,
              referral earnings, and cashback credit, with Invoices and Payout setup (via a hosted card-payout flow or
              manual bank details) alongside it. Share your personal referral code and earn <strong>2% of every job</strong>{' '}
              a referred user completes, for 12 months of their activity.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="workshops" title="For Workshops">
          <p>
            Workshops quote on vehicle repair jobs rather than deliveries — the same marketplace, priced around labour
            and parts instead of a single mileage-based figure.
          </p>

          <Topic title="Quote on a repair, itemised">
            <p>
              A repair quote is always split into <strong>Labour</strong> and <strong>Parts</strong>, both entered inc.
              VAT — the customer sees exactly what they're paying for your time versus components.
            </p>
          </Topic>

          <Topic title="Complete the repair">
            <p>
              Once accepted and paid, start the repair and submit completion evidence — photos plus optional notes on the
              work done. Repair jobs skip the GPS/signature/recipient-name steps a delivery needs, since there's no
              handover in transit to verify.
            </p>
          </Topic>

          <Topic title="Auto-quote your standard services, for free">
            <p>
              Four services can be priced once and quoted automatically the instant a matching job appears, without you
              looking at the vehicle first: <strong>MOT, Annual Service/Inspection, Servicing, and Tyres</strong>. Set
              labour hours and rate plus any parts cost, and it's done — free to configure. Anything that genuinely needs
              eyes on the vehicle first (general repairs, diagnostics, bodywork) always comes to you as a normal job to
              price by hand.
            </p>
          </Topic>

          <Topic title="Get paid, and build your reputation">
            <p>
              The same Payments Board, Invoices, Wallet, and Payout setup available to Carriers applies to Workshops too.
              Customers rate your repair work once a job completes, and you can also import an external reputation — a
              verified screenshot of your Checkatrade, Trustpilot, or Google rating — onto your public profile.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="bazaar" title="The Bazaar Marketplace">
          <p>
            Open to every role — Customers, Carriers, and Workshops can all buy and sell. There are two listing types:{' '}
            <strong>Bazaar</strong> (a real item for sale, with offers, a basket, and checkout) and{' '}
            <strong>Classifieds</strong> (a contact-only ad for services, jobs, or announcements).
          </p>

          <Topic title="List an item for sale — free">
            <p>
              Add photos and video, a title and description, a category — which brings in category-specific fields
              automatically, like mileage and fuel type for a vehicle — a price, and how you'll handle delivery
              (collection only, included in the price, or a separate delivery charge you set). Choose Fixed price or Open
              to offers. AI Assist can fill in whatever's left blank from your photos and a few keywords, without ever
              touching what you've already typed yourself. Listing a Sell item costs nothing.
            </p>
          </Topic>

          <Topic title="Buy — browse, offer, or buy now">
            <p>
              Search by category, price, and postcode/radius. On an Open-pricing listing you can make an offer; on any
              Sell listing you can Buy Now for a single item or add several — even from different sellers — into one
              basket and checkout together. At checkout you choose how much to pay online versus Cash on Delivery, up to
              paying in full. Once paid, a one-click link lets you post a delivery job for the item straight away,
              bridging your purchase directly into TransitDeal's delivery marketplace.
            </p>
          </Topic>

          <Topic title="Message the seller, and manage your own sales">
            <p>
              Chat directly with a seller from any Bazaar listing before you commit. As a seller, <strong>My
              Bazaar</strong> covers your listings (cancel, or accept/decline offers), your orders (confirm a Cash-on-
              Delivery payment using the buyer's code), and your sales (what's owed, released, or still to collect) —
              with a prompt to connect a payout account the first time you need one.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="chat" title="Chat &amp; the AI Assistant">
          <p>TransitDeal has two human-to-human chats and two AI-driven ones — all free to use as much as you like.</p>

          <Topic title="Job Chat &amp; Bazaar Chat">
            <p>
              Message a carrier or workshop directly from a job page — even before you've accepted their quote — with
              voice-to-text dictation, an "AI Improve" button to tidy up your draft, and read receipts. A simpler
              text-only chat lets a Bazaar buyer message a seller from any listing.
            </p>
          </Topic>

          <Topic title="The AI Chatbot">
            <p>
              A general help assistant that can hold a natural conversation to post a delivery job, walk a new driver
              through completing their profile step by step, or answer questions about how the platform works. Ask it
              about uploading an external rating and it opens the upload form right inside the chat, without you leaving
              the conversation.
            </p>
          </Topic>

          <Topic title="The Voice Assistant">
            <p>
              A microphone-driven version of the same assistant, tailored to guided registration, job-posting, and
              quote-submission flows — recording continues until you stop it, with a live transcript shown as you speak,
              and replies read back to you.
            </p>
          </Topic>
        </Chapter>

        <Chapter id="free" title="What's Free">
          <p>
            TransitDeal has <strong>no membership fee</strong>, on any account type, permanently. Registration, browsing,
            posting, and quoting all cost nothing — you only ever pay for the job, repair, or item you actually agree to.
            A few features go further, and exist specifically to make it easy to get comfortable on the platform before
            you commit to anything:
          </p>
          <Ul items={[
            <><strong>Internal Fleet Jobs</strong> — run your own fleet's deliveries through the platform's full execution toolkit (live GPS tracking, Proof of Collection, Proof of Delivery) with no bidding and no payment step at all.</>,
            <><strong>The Employee Portal</strong> — add as many drivers or mechanics as you like, each with their own free personal job-running link.</>,
            <><strong>Auto-Quote</strong> — a complete automatic pricing tool, including its test Simulator, free to build and run.</>,
            <><strong>Available Loads &amp; Job Alerts</strong> — posting spare capacity, and getting notified of matching work, cost nothing to use.</>,
            <><strong>Listing an item for sale on the Bazaar</strong> — a standard Sell listing is free to post.</>,
            <><strong>The Wallet</strong> — spend your balance with no transfer fee between members, and top it up for free by bank transfer.</>,
            <><strong>The security-deposit eligibility route</strong> — fully refundable after 10 successful jobs and a good rating, so even this comes back to you.</>,
            <><strong>Cashback</strong> — new Customers get 5% cashback on each of their first three completed jobs, applied automatically to their next payment.</>,
            <><strong>Referrals</strong> — earn 2% of every job a referred user completes, for 12 months of their activity, with no cap on customer referrals.</>,
          ]} />
        </Chapter>

      </div>
    </div>
  );
}
